import { createContext, useContext, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { manproQueryKeys, providerOpportunityService } from '../api/manpro';

interface ProviderFavoriteContextType {
  favoriteIds: string[];
  isLoading: boolean;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  isToggling: boolean;
}

const ProviderFavoriteContext = createContext<ProviderFavoriteContextType | undefined>(undefined);

export const ProviderFavoriteProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();

  const favoritesQuery = useQuery({
    queryKey: manproQueryKeys.providerOpportunityFavorites(),
    queryFn: providerOpportunityService.getFavoritesIds,
    enabled: localStorage.getItem('userRole') === 'provider',
  });

  const favoriteIds = favoritesQuery.data ?? [];
  const isLoading = favoritesQuery.isLoading;

  const isFavorite = (id: string) => favoriteIds.includes(id);

  const toggleFavoriteMutation = useMutation({
    mutationFn: async (projectId: string) => {
      if (isFavorite(projectId)) {
        await providerOpportunityService.removeFavorite(projectId);
        return { projectId, action: 'removed' };
      } else {
        await providerOpportunityService.addFavorite(projectId);
        return { projectId, action: 'added' };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.providerOpportunityFavorites() });
      // Juga invalidate daftar favorites pagination agar refresh
      queryClient.invalidateQueries({ queryKey: ['manpro', 'provider', 'opportunities', 'favorites'] });
      
      if (data.action === 'added') {
        toast.success('Berhasil menambahkan ke favorit.');
      } else {
        toast.success('Berhasil menghapus dari favorit.');
      }
    },
    onError: () => {
      toast.error('Gagal memperbarui favorit. Silakan coba lagi.');
    },
  });

  const toggleFavorite = (id: string) => {
    toggleFavoriteMutation.mutate(id);
  };

  return (
    <ProviderFavoriteContext.Provider
      value={{
        favoriteIds,
        isLoading,
        isFavorite,
        toggleFavorite,
        isToggling: toggleFavoriteMutation.isPending,
      }}
    >
      {children}
    </ProviderFavoriteContext.Provider>
  );
};

export const useProviderFavorite = () => {
  const context = useContext(ProviderFavoriteContext);
  if (context === undefined) {
    throw new Error('useProviderFavorite must be used within a ProviderFavoriteProvider');
  }
  return context;
};
